import { createEntityAdapter, createSlice, PayloadAction, createSelector } from "@reduxjs/toolkit";
import { AwarenessCampaignData } from "../../../models/AwarenessCampaign";
import { LoadableContext, RootState } from "../../store";
import { isCampaignCompleted } from '../../../utils/campaignHelpers';

const awarenessCampaignsAdapter = createEntityAdapter<AwarenessCampaignData>({
    selectId: awarenessCampaign => awarenessCampaign.ID,
    sortComparer: (awarenessCampaign1, awarenessCampaign2) => {
        return awarenessCampaign1.name.localeCompare(awarenessCampaign2.name)
    }
});

interface AwarenessCampaignsState extends LoadableContext {
    showAmbassadorCompleted: boolean;
}

const initialState: AwarenessCampaignsState = {
    loading: false,
    error: null,
    showAmbassadorCompleted: false, // Default to hiding missions where ambassador status is completed
};

export const awarenessCampaignsSlice = createSlice({
    name: 'awareness_campaigns_list',
    initialState: awarenessCampaignsAdapter.getInitialState(initialState),
    reducers: {
        startLoadingList: (state) => {
            state.loading = true;
            state.error = null;
        },
        setList: (state, { payload: awarenessCampaigns }: PayloadAction<AwarenessCampaignData[]>) => {
            state.loading = false;
            awarenessCampaignsAdapter.setAll(state, awarenessCampaigns);
        },
        addItem: (state, { payload: awarenessCampaign }: PayloadAction<AwarenessCampaignData>) => {
            state.loading = false;
            awarenessCampaignsAdapter.addOne(state, awarenessCampaign);
        },
        updateItem: (state, { payload: { ID, data } }: PayloadAction<{ ID: string, data: Partial<AwarenessCampaignData> }>) => {
            awarenessCampaignsAdapter.updateOne(state, {
                id: ID,
                changes: data,
            });
        },
        setError: (state, { payload }: PayloadAction<string>) => {
            state.loading = false;
            state.error = payload;
        },
        toggleShowAmbassadorCompleted: (state) => {
            state.showAmbassadorCompleted = !state.showAmbassadorCompleted;
        },
    },
});

export const AwarenessCampaignsActions = awarenessCampaignsSlice.actions;

// Basic selectors
export const {
    selectAll: selectAllAwarenessCampaigns,
    selectById: selectAwarenessCampaignById,
    selectIds: selectAwarenessCampaignsIds
} = awarenessCampaignsAdapter.getSelectors((state: RootState) => state.awareness_campaign.list);

// Selector for filtered missions based on ambassador's completion status
export const selectFilteredAwarenessCampaigns = createSelector(
    [
        selectAllAwarenessCampaigns,
        (state: RootState) => state.awareness_campaign.list.showAmbassadorCompleted,
        (state: RootState) => state.user.email
    ],
    (missions, showCompleted, userEmail) => {
        if (!userEmail) return missions;
        
        return missions.filter(mission => {
            const isCompleted = isCampaignCompleted(mission, userEmail);
            return showCompleted ? isCompleted : !isCompleted;
        });
    }
);

const AwarenessCampaignsReducer = awarenessCampaignsSlice.reducer;

export default AwarenessCampaignsReducer;